import { Box, Stack, useTheme } from '@mui/material';
import { ReactNode } from 'react';

import { PresentationSlide } from '@/modules/presentation/PresentationSlide';

import {
  PresentationCoverSlideConfig,
  SlideConfigVariant,
  SlideHoverHaloRefs,
  TenantSlideStyle,
} from '../coverSlide/CoverSlide.types';
import { useMergedSlideDesign } from '../hooks/useMergedSlideDesign';
import {
  SLIDE_CONTENT_TOP_OFFSET_SPACING,
  SLIDE_TITLE_TO_CONTENT_SPACING,
} from '../shared/slideContentSpacing';
import {
  CustomPositionedSlideLogo,
  getSlideLogoState,
  SlideLogomark,
} from '../shared/slideLogo';
import { getSlideTextHorizontalPosition } from '../shared/slideTextPosition';
import { useInlineLogoHaloExtension } from '../shared/useInlineLogoHaloExtension';
import { SlideEditorRichTextEditor } from '../textEditors/SlideEditor.richTextEditor';
import { SlideEditorTitleTextEditor } from '../textEditors/SlideEditor.titleTextEditor';
import { SlideEditorVariableButton } from '../textEditors/SlideEditor.variableButton';

export interface DividerSlideProps {
  Slide?: typeof PresentationSlide.Slide;
  dividerCopy: string;
  customContent?: string;
  onChange?: (newCopy: string) => void;
  onCustomContentChange?: (newCustomContent: string) => void;
  /**
   * Per-keystroke variants of the change handlers above, each forwarding the
   * editor's current value (unlike the `onChange` handlers, which commit on
   * blur). The template editor uses these to track in-progress edits and arm
   * the unsaved-changes blocker before the user navigates away.
   */
  onInput?: (value: string) => void;
  onCustomContentInput?: (value: string) => void;
  onClickInput?: () => void;
  highlightVariables?: boolean;
  /** Optional prop-level design overrides (highest priority in merge). */
  tenantSlideDesign?: PresentationCoverSlideConfig;
  /** Slide ID for scroll-to targeting. */
  slideId?: string;
  /** Optional footer rendered at the bottom of the slide. */
  footer?: ReactNode;
  /**
   * Optional content rendered inside the slide's clipped content area
   * alongside the title block — used by the admin branding preview to
   * mount drag overlays that need to live inside the slide's scale
   * transform so their coordinates align with the title-block math.
   */
  slideOverlay?: ReactNode;
  /**
   * Optional refs to internal elements that the admin branding preview
   * uses to drive its hover-halo indicator. See `SlideHoverHaloRefs`.
   */
  hoverHaloRefs?: SlideHoverHaloRefs;
}

export function DividerSlide({
  dividerCopy,
  customContent = '',
  onChange,
  onCustomContentChange,
  onInput,
  onCustomContentInput,
  onClickInput,
  highlightVariables,
  tenantSlideDesign: tenantSlideDesignProp,
  slideId,
  footer,
  Slide = PresentationSlide.Slide,
  slideOverlay,
  hoverHaloRefs,
}: DividerSlideProps) {
  const theme = useTheme();

  // Merge prop > divider tenant > cover tenant > defaults at the field level
  const tenantSlideDesign = useMergedSlideDesign(
    SlideConfigVariant.DIVIDER,
    tenantSlideDesignProp
  );

  const tenantStyle: TenantSlideStyle | undefined =
    tenantSlideDesign?.textColor ||
    tenantSlideDesign?.titleTextSize ||
    tenantSlideDesign?.titleSizeRem != null ||
    tenantSlideDesign?.bodyTextColor ||
    tenantSlideDesign?.bodyTextAlignment
      ? {
          textColor: tenantSlideDesign.textColor,
          titleTextSize: tenantSlideDesign.titleTextSize,
          titleSizeRem: tenantSlideDesign.titleSizeRem,
          bodyTextColor: tenantSlideDesign.bodyTextColor,
          bodyTextAlignment: tenantSlideDesign.bodyTextAlignment,
        }
      : undefined;

  // T1-5959: shared with CoverSlide via slideLogo helpers.
  const {
    logoScaleFactor,
    useDefaultLogoPosition,
    logoOriginX,
    logoOriginY,
    textOriginX,
    textOriginY,
  } = getSlideLogoState(tenantSlideDesign);

  // Mirrors CoverSlide: suppress every logo render path when the admin
  // has opted out of showing the logo, including the reserved slot
  // that normally keeps the title baseline stable.
  const hideLogo = tenantSlideDesign?.hideLogo === true;

  const textHorizontalPosition = getSlideTextHorizontalPosition(
    theme.spacing(10.5),
    textOriginX,
    tenantSlideDesign?.bodyTextAlignment,
    tenantSlideDesign?.textWidth
  );

  // Used only as the hidden placeholder when `useDefaultLogoPosition` is
  // false. Matches the visible inline logo's `enforceMaxHeight` so the
  // reserved layout space lines up with what the visible logo would
  // occupy — toggling the checkbox keeps the title baseline still.
  const logoElement = (
    <SlideLogomark
      scaleFactor={logoScaleFactor}
      enforceMaxHeight
      useDarkLogo={tenantSlideDesign?.useDarkLogo}
    />
  );

  // See CoverSlide for the rationale.
  const { ref: inlineLogoRef, extensionPx: haloScaleExtensionPx } =
    useInlineLogoHaloExtension({
      enabled: !!hoverHaloRefs && useDefaultLogoPosition && !hideLogo,
      scaleFactor: logoScaleFactor,
    });

  const content = (
    <Stack
      sx={{
        height: '100%',
        width: '100%',
        position: 'relative',
        pointerEvents: 'auto',
      }}
    >
      <Stack
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'clip',
        }}
      >
        {!useDefaultLogoPosition && !hideLogo && tenantSlideDesign && (
          <CustomPositionedSlideLogo
            ref={hoverHaloRefs?.customLogo}
            slideName="DividerSlide"
            useDarkLogo={tenantSlideDesign.useDarkLogo}
            scaleFactor={logoScaleFactor}
            logoOriginX={logoOriginX}
            logoOriginY={logoOriginY}
          />
        )}

        {/* Title block: calc-based positioning matches CoverSlide. */}
        <Stack
          data-name="DividerSlide.TitleBlock"
          sx={{
            position: 'absolute',
            top: `calc(${theme.spacing(SLIDE_CONTENT_TOP_OFFSET_SPACING)} + ${textOriginY}px)`,
            ...textHorizontalPosition,
          }}
        >
          {/* Invisible placeholder when the admin has opted out of the
              default logo position. Lives outside the halo target so
              the admin hover halo wraps only the visible content while
              still reserving the logo's vertical space here, keeping
              the title baseline stable when the checkbox is toggled. */}
          {tenantSlideDesign && !useDefaultLogoPosition && !hideLogo && (
            <Box mb="3vh !important" sx={{ visibility: 'hidden' }}>
              {logoElement}
            </Box>
          )}
          <Box
            ref={hoverHaloRefs?.titleHaloTarget}
            data-name="DividerSlide.TitleHaloTarget"
            sx={
              haloScaleExtensionPx > 0
                ? {
                    marginTop: `-${haloScaleExtensionPx}px`,
                    paddingTop: `${haloScaleExtensionPx}px`,
                  }
                : undefined
            }
          >
            <Stack direction="column" spacing={theme.spacing(1)}>
              {/* Visible inline logo — only rendered when the default
                  position is on. Scale transform lives on the wrapper
                  (with `applyScale={false}` on the inner Logomark)
                  so the wrapper's visual is anchored from its
                  bottom-left while the layout box stays at natural
                  bounds. The hover halo wraps the parent
                  `TitleHaloTarget`, which the slide extends upward to
                  match the scaled visual when this case is active. */}
              {tenantSlideDesign && useDefaultLogoPosition && !hideLogo && (
                <Box
                  ref={inlineLogoRef}
                  mb="3vh !important"
                  sx={{
                    transform: `scale(${logoScaleFactor})`,
                    transformOrigin: 'left bottom',
                  }}
                >
                  <SlideLogomark
                    useDarkLogo={tenantSlideDesign?.useDarkLogo}
                    scaleFactor={logoScaleFactor}
                    applyScale={false}
                    enforceMaxHeight
                  />
                </Box>
              )}
              {/* T1-5995: zIndex sits on the text wrapper (not the
                  title-block container) so the custom-positioned logo
                  only renders behind the text, never behind the
                  default-position logo above. */}
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <SlideEditorTitleTextEditor
                  title={dividerCopy}
                  onChange={onChange}
                  onInput={onInput}
                  onClickInput={onClickInput}
                  placeholder="[Divider title]"
                  highlightVariables={highlightVariables}
                  tenantStyle={tenantStyle}
                  allowLineBreaks
                />
              </Box>
            </Stack>
            <Box
              mt={`${SLIDE_TITLE_TO_CONTENT_SPACING} !important`}
              sx={{ position: 'relative', zIndex: 1 }}
            >
              <SlideEditorRichTextEditor
                content={customContent}
                onSubmit={onCustomContentChange}
                onRawUpdate={onCustomContentInput}
                onClick={onClickInput}
                highlightVariables={highlightVariables}
                tenantStyle={tenantStyle}
                additionalToolbarButtons={<SlideEditorVariableButton />}
              />
            </Box>
          </Box>
        </Stack>
        {slideOverlay}
      </Stack>
    </Stack>
  );

  if (slideId) {
    return (
      <Slide
        id={slideId}
        slideDesign={tenantSlideDesign ?? {}}
        slideBackgroundColor={tenantSlideDesign?.backgroundColor}
        slideBackgroundImage={tenantSlideDesign?.backgroundImagePath}
        slideBackgroundAlpha={tenantSlideDesign?.backgroundImageAlpha}
        slideBackgroundOriginX={tenantSlideDesign?.backgroundImageOriginX}
        slideBackgroundOriginY={tenantSlideDesign?.backgroundImageOriginY}
        slideBackgroundScale={tenantSlideDesign?.backgroundImagePercentScale}
        footer={tenantSlideDesign?.includeFooter === false ? null : footer}
      >
        {content}
      </Slide>
    );
  }

  return content;
}
